import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { postAsync } from '@/helpers/http-request'

type ResetPasswordData = {
  userId: string;
  token: string;
  password: string;
};

type ResetPasswordErrorType = 'passwordTooWeak' | 'invalidToken' | 'unknownResetError';

type ResetPasswordError = {
  type: ResetPasswordErrorType;
}

const useResetPassword = () => {
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async (data: ResetPasswordData): Promise<void> => {
      const response = await postAsync('/web/v1/user/resetPassword', data);

      if (response.ok) return;

      const responseData = await response.json();
      switch (responseData.result) {
        case 1:
          throw { type: 'passwordTooWeak' };
        case 2:
          throw { type: 'invalidToken' };
        default:
          throw { type: 'unknownResetError' };
      }
    },
  });

  const getErrorMessage = (): string | null => {
    if (!mutation.error) return null
    const error = mutation.error as unknown as ResetPasswordError
    return t(`errors.${error.type}`)
  }

  return {
    resetPassword: mutation.mutateAsync,
    resetPasswordAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: getErrorMessage(),
    reset: mutation.reset,
  };
}

export default useResetPassword;